import React, { useEffect } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { trackPageView, setUiVersion } from './services/analytics';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Register from './pages/Register';
import Overview from './pages/Overview';
import ViewInsights from './pages/ViewInsights';
import AddProduct from './pages/AddProduct';
import ViewProducts from './pages/ViewProducts';
import ViewFavoriteProducts from './pages/ViewFavoriteProducts';
import EditProduct from './pages/EditProduct';
import AddProductAB from './pages/AddProductAB';
import EditProductOffering from './pages/EditProductOffering';
import AddInventory from './pages/AddInventory';
import EditInventory from './pages/EditInventory';
import ViewInventory, { ReorderInventory } from './pages/ViewInventory';
import ViewReorders from './pages/ViewReorders';
import AddShop from './pages/AddShop';
import EditShop from './pages/EditShop';
import ViewShops from './pages/ViewShops';
import AddUnit from './pages/AddUnit';
import ViewUnits from './pages/ViewUnits';
import EditUnit from './pages/EditUnit';
import DashboardLayout from './components/DashboardLayout';
import AddProductOffering from './pages/AddProductOffering';
import ViewProductOfferings from './pages/ViewProductOfferings';
import AddMarket from './pages/AddMarket';
import EditMarket from './pages/EditMarket';
import ViewMarkets from './pages/ViewMarkets';
import AddPurchase from './pages/AddPurchase';
import EditPurchase from './pages/EditPurchase';
import ViewPurchases from './pages/ViewPurchases';
import AddSupplier from './pages/AddSupplier';
import EditSupplier from './pages/EditSupplier';
import ViewSuppliers from './pages/ViewSuppliers';
import AddSale from './pages/AddSale';
import EditSale from './pages/EditSale';
import ViewSales from './pages/ViewSales';
import EnterFavoriteSales from './pages/EnterFavoriteSales';
import ReconcileInventory from './pages/ReconcileInventory';
import TestPage from './pages/testPage';
import CustomerTransaction from './pages/CustomerTransaction'
import ClipDemo from './pages/ClipDemo';
import PrivateRoute from './components/PrivateRoute';

// New UI (v2) — parallel routes under /new/*
import LoginV2 from './pages-v2/LoginV2';
import RegisterV2 from './pages-v2/RegisterV2';
import ForgotPasswordV2 from './pages-v2/ForgotPasswordV2';
import DashboardLayoutV2 from './layouts/DashboardLayoutV2';
import OverviewV2 from './pages-v2/OverviewV2';
import AddSaleV2 from './pages-v2/AddSaleV2';
import ViewSalesV2 from './pages-v2/ViewSalesV2';
import AddUnitV2 from './pages-v2/AddUnitV2';
import ViewUnitsV2 from './pages-v2/ViewUnitsV2';
import AddPurchaseV2 from './pages-v2/AddPurchaseV2';
import ViewPurchasesV2 from './pages-v2/ViewPurchasesV2';
import AddSupplierV2 from './pages-v2/AddSupplierV2';
import ViewSuppliersV2 from './pages-v2/ViewSuppliersV2';
import AddInventoryV2 from './pages-v2/AddInventoryV2';
import ViewInventoryV2 from './pages-v2/ViewInventoryV2';
import AddProductV2 from './pages-v2/AddProductV2';
import ViewProductsV2 from './pages-v2/ViewProductsV2';
import ViewFavoriteProductsV2 from './pages-v2/ViewFavoriteProductsV2';
import InsightsV2 from './pages-v2/InsightsV2';
import TransactionV2 from './pages-v2/TransactionV2';
import ReorderInventoryV2 from './pages-v2/ReorderInventoryV2';
import EditSaleV2 from './pages-v2/EditSaleV2';
import EditPurchaseV2 from './pages-v2/EditPurchaseV2';
import EditUnitV2 from './pages-v2/EditUnitV2';
import EditProductV2 from './pages-v2/EditProductV2';
import EditSupplierV2 from './pages-v2/EditSupplierV2';
import ReconcileInventoryV2 from './pages-v2/ReconcileInventoryV2';
import ViewReordersV2 from './pages-v2/ViewReordersV2';

// Admin-specific pages
import UserList from './pages/admin/UserList';
import UserDetails from './pages/admin/UserDetails';
import UserPurchases from './pages/admin/UserPurchases';
import UserSales from './pages/admin/UserSales';

import { ShopProvider } from './context/ShopContext';
import theme from './theme';

const App = () => {
  const role = localStorage.getItem('role');
  const isAdmin = role === 'admin';
  const location = useLocation();

  // Track page views on route change
  useEffect(() => {
    const pageName = location.pathname.split('/').filter(Boolean).join(' > ') || 'home';
    // Tag every event with which UI is in use: /new/* is the V2 UI, else V1.
    const isV2 = location.pathname.startsWith('/new');
    setUiVersion(isV2 ? 'v2' : 'v1');
    trackPageView(pageName);

    // Point the PWA manifest at the V2 variant while in the new UI, so iOS
    // "Add to Home Screen" pins /new/login (manifest-new.json start_url)
    // instead of the V1 root that the shared manifest.json resolves to.
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      manifestLink.setAttribute('href', isV2 ? '/manifest-new.json' : '/manifest.json');
    }
  }, [location]);

  return (
    <ThemeProvider theme={theme}>
      <ShopProvider>
        <CssBaseline />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/testpage" element={<TestPage />} />
          <Route path="/register" element={<Register />} />
          <Route element={<PrivateRoute />}>
            <Route path="/dashboard" element={<DashboardLayout isAdmin={isAdmin} />}>
              <Route index element={<Overview />} />
              <Route path="insights" element={<ViewInsights />} />
              <Route path="products/add" element={<AddProduct />} />
              <Route
                path="products/add-ab"
                element={
                  // Remove the left gap only for this page by offsetting typical main padding (≈24px)
                  <div style={{ marginLeft: -220, marginRight: -24, marginTop: -24 }}>
                    <AddProductAB />
                  </div>
                }
              />
              <Route path="products/view" element={<ViewProducts />} />
              <Route path="products/favorites" element={<ViewFavoriteProducts />} />
              <Route path="products/edit/:id" element={<EditProduct />} />
              <Route path="inventories/add" element={<AddInventory />} />
              <Route path="inventories/stock-entry" element={<AddInventory />} />
              <Route path="inventories/edit/:id" element={<EditInventory />} />
              <Route path="inventories/view" element={<ViewInventory />} />
              <Route path="inventories/reorder" element={<ReorderInventory />} />
              <Route path="inventories/view-reorders" element={<ViewReorders />} />
              <Route path="shops/add" element={<AddShop />} />
              <Route path="shops/edit/:id" element={<EditShop />} />
              <Route path="shops/view" element={<ViewShops />} />
              <Route path="units/add" element={<AddUnit />} />
              <Route path="units/edit/:id" element={<EditUnit />} />
              <Route path="units/view" element={<ViewUnits />} />
              <Route path="productOfferings/add" element={<AddProductOffering />} />
              <Route path="productOfferings/edit/:id" element={<EditProductOffering />} />
              <Route path="productOfferings/view" element={<ViewProductOfferings />} />
              <Route path="markets/add" element={<AddMarket />} />
              <Route path="markets/edit/:id" element={<EditMarket />} />
              <Route path="markets/view" element={<ViewMarkets />} />
              <Route path="purchases/add" element={<AddPurchase />} />
              <Route path="purchases/edit/:id" element={<EditPurchase />} />
              <Route path="purchases/view" element={<ViewPurchases />} />
              <Route path="suppliers/add" element={<AddSupplier />} />
              <Route path="suppliers/edit/:id" element={<EditSupplier />} />
              <Route path="suppliers/view" element={<ViewSuppliers />} />
              <Route path="sales/add" element={<AddSale />} />
              <Route path="sales/edit/:id" element={<EditSale />} />
              <Route path="sales/view" element={<ViewSales />} />
              <Route path="sales/favorites" element={<EnterFavoriteSales />} />
              <Route path="inventories/reconcile/:id" element={<ReconcileInventory />} />
              <Route path="customertransaction" element={<CustomerTransaction />} />
              <Route path="clip-demo" element={<ClipDemo />} />

              {/* Admin-specific routes */}
              {isAdmin && (
                <>
                  <Route path="admin/users" element={<UserList />} />
                  <Route path="admin/users/:id/activity" element={<UserDetails />} />
                  <Route path="admin/users/purchases" element={<UserPurchases/>} />
                  <Route path="admin/users/sales" element={<UserSales/>} />
                </>
              )}
            </Route>
          </Route>
          {/* ── New UI (v2) — parallel, no impact on existing routes ── */}
          <Route path="/new/login" element={<LoginV2 />} />
          <Route path="/new/register" element={<RegisterV2 />} />
          <Route path="/new/forgot-password" element={<ForgotPasswordV2 />} />
          <Route element={<PrivateRoute />}>
            <Route path="/new/dashboard" element={<DashboardLayoutV2 />}>
              <Route index element={<OverviewV2 />} />
              <Route path="sales/add" element={<AddSaleV2 />} />
              <Route path="sales/view" element={<ViewSalesV2 />} />
              <Route path="units/add" element={<AddUnitV2 />} />
              <Route path="units/view" element={<ViewUnitsV2 />} />
              <Route path="purchases/add" element={<AddPurchaseV2 />} />
              <Route path="purchases/view" element={<ViewPurchasesV2 />} />
              <Route path="suppliers/add" element={<AddSupplierV2 />} />
              <Route path="suppliers/view" element={<ViewSuppliersV2 />} />
              <Route path="inventories/add" element={<AddInventoryV2 />} />
              <Route path="inventories/view" element={<ViewInventoryV2 />} />
              <Route path="products/add" element={<AddProductV2 />} />
              <Route path="products/view" element={<ViewProductsV2 />} />
              <Route path="products/favorites" element={<ViewFavoriteProductsV2 />} />
              <Route path="insights" element={<InsightsV2 />} />
              <Route path="transactions" element={<TransactionV2 />} />
              <Route path="reorder" element={<ReorderInventoryV2 />} />
              <Route path="inventories/view-reorders" element={<ViewReordersV2 />} />
              <Route path="sales/edit/:id" element={<EditSaleV2 />} />
              <Route path="purchases/edit/:id" element={<EditPurchaseV2 />} />
              <Route path="units/edit/:id" element={<EditUnitV2 />} />
              <Route path="products/edit/:id" element={<EditProductV2 />} />
              <Route path="suppliers/edit/:id" element={<EditSupplierV2 />} />
              <Route path="inventories/reconcile/:id" element={<ReconcileInventoryV2 />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </ShopProvider>
    </ThemeProvider>
  );
};

export default App;
